/**
 * LVS 77-3:2016 character widths (s) at reference height h = 350 mm.
 *
 * Source: LVS 77-3:2016, Table 9.2 (Font A "Parastais raksts") and
 *         Table 9.3 (Font B "Šaurais raksts").
 *
 * Latvian diacritic letters (Ā, Č, Ē, Ģ, Ī, Ķ, Ļ, Ņ, Š, Ū, Ž and their
 * lowercase equivalents) inherit the width of their base Latin letter,
 * per the standard.
 *
 * All values are in millimetres. To get the width at any other reference
 * height h, multiply by h / 350.
 */

/** Font A — "Parastais raksts" (regular) — Table 9.2 */
export const WIDTHS_A: Readonly<Record<string, number>> = {
    // Uppercase Latin
    A: 300,
    B: 234.75,
    C: 248.0709,
    D: 250,
    E: 225,
    F: 225,
    G: 250,
    H: 250,
    I: 53.125,
    J: 203.125,
    K: 275,
    L: 225,
    M: 300,
    N: 262.5,
    O: 250,
    P: 234.75,
    Q: 284.585,
    R: 256.25,
    S: 256.25,
    T: 246.875,
    U: 250,
    V: 275,
    W: 400,
    X: 275,
    Y: 262.5,
    Z: 225,

    // Uppercase Latvian (same width as base letter)
    Ā: 300,
    Č: 248.0709,
    Ē: 225,
    Ģ: 250,
    Ī: 53.125,
    Ķ: 275,
    Ļ: 225,
    Ņ: 262.5,
    Š: 256.25,
    Ū: 250,
    Ž: 225,

    // Lowercase Latin
    a: 200,
    b: 200,
    c: 199.04625,
    d: 200,
    e: 206.25,
    f: 125,
    g: 200,
    h: 200,
    i: 50,
    j: 93.75,
    k: 218.75,
    l: 87.5,
    m: 350,
    n: 200,
    o: 206.25,
    p: 200,
    q: 200,
    r: 168.75,
    s: 212.5,
    t: 112.5,
    u: 200,
    v: 222,
    w: 350,
    x: 225,
    y: 225,
    z: 187.5,

    // Lowercase Latvian (same width as base letter)
    ā: 200,
    č: 199.04625,
    ē: 206.25,
    ģ: 200,
    ī: 50,
    ķ: 218.75,
    ļ: 87.5,
    ņ: 200,
    š: 212.5,
    ū: 200,
    ž: 187.5,

    // Digits
    '0': 200,
    '1': 100,
    '2': 200,
    '3': 205.48968,
    '4': 225,
    '5': 200,
    '6': 200,
    '7': 200,
    '8': 212.5,
    '9': 200,

    // Symbols
    '?': 187.5,
    '!': 62.5,
    '/': 150,
    '(': 96.887187,
    ')': 96.887187,
    '%': 200,
    '+': 150,
    '-': 150,
    '.': 50,
    ',': 50,
    ':': 50,
    ';': 50,
    '"': 125,
    '>': 100,
    '<': 100
}

/** Font B — "Šaurais raksts" (narrow) — Table 9.3 */
export const WIDTHS_B: Readonly<Record<string, number>> = {
    // Uppercase Latin
    A: 200,
    B: 162.5,
    C: 162.5,
    D: 162.5,
    E: 150,
    F: 150,
    G: 168.75,
    H: 162.5,
    I: 50,
    J: 150,
    K: 187.5,
    L: 150,
    M: 225,
    N: 175,
    O: 168.75,
    P: 162.5,
    Q: 196.758,
    R: 181.25,
    S: 171.875,
    T: 168.75,
    U: 162.5,
    V: 187.5,
    W: 275,
    X: 193.75,
    Y: 187.5,
    Z: 150,

    // Uppercase Latvian (same width as base letter)
    Ā: 200,
    Č: 162.5,
    Ē: 150,
    Ģ: 168.75,
    Ī: 50,
    Ķ: 187.5,
    Ļ: 150,
    Ņ: 175,
    Š: 171.875,
    Ū: 162.5,
    Ž: 150,

    // Lowercase Latin
    a: 150,
    b: 150,
    c: 150,
    d: 150,
    e: 150,
    f: 112.5,
    g: 150,
    h: 150,
    i: 50,
    j: 81.25,
    k: 175,
    l: 75,
    m: 250,
    n: 150,
    o: 150,
    p: 150,
    q: 150,
    r: 115.625,
    s: 150,
    t: 106.25,
    u: 150,
    v: 175,
    w: 262.5,
    x: 175,
    y: 175,
    z: 137.5,

    // Lowercase Latvian (same width as base letter)
    ā: 150,
    č: 150,
    ē: 150,
    ģ: 150,
    ī: 50,
    ķ: 175,
    ļ: 75,
    ņ: 150,
    š: 150,
    ū: 150,
    ž: 137.5,

    // Digits
    '0': 150,
    '1': 100,
    '2': 150,
    '3': 150,
    '4': 175,
    '5': 150,
    '6': 150,
    '7': 150,
    '8': 150,
    '9': 150,

    // Symbols
    '?': 150,
    '!': 62.5,
    '/': 100,
    '(': 74.609,
    ')': 74.609,
    '%': 150,
    '+': 150,
    '-': 150,
    '.': 50,
    ',': 50,
    ':': 50,
    ';': 50,
    '"': 100,
    '>': 100,
    '<': 100
}
